import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Check, X, ArrowRight, ShieldCheck, Video, Phone, 
  Search, AlertTriangle, TrendingUp, Target, Zap,
  CreditCard, ArrowLeft, Scale, Package, BarChart3,
  BookOpen, FileText, Users, CheckCircle, XCircle, 
  Clock, Rocket, DollarSign, Layers
} from 'lucide-react';
import CheckoutModal from '../components/landing/CheckoutModal';
import AfterPaymentBlock from '../components/catalog/AfterPaymentBlock';
import { usePrices } from '../context/PricesContext';

// Before/After items
const beforeItems = [
  { text: 'Часами ищешь поставщиков без ответа' },
  { text: 'Пишешь брендам вслепую → 3–5% ответов' },
  { text: 'Не понимаешь, какие бренды реально подходят под опт' },
  { text: 'Keepa = графики, но не инструмент решений' },
  { text: 'Тонны ASINов → мало нормальных' },
  { text: 'Сорсинг не масштабируется' },
];

const afterItems = [
  { text: 'Понимаешь, где и как искать оптовых поставщиков' },
  { text: 'Получаешь 15–20% ответов от брендов и дистрибьюторов' },
  { text: 'Отбираешь бренды осознанно, а не «Nike и надеюсь»' },
  { text: 'Перерабатываешь сотни и тысячи ASINов по чётким фильтрам' },
  { text: 'Видишь только товары, которые подходят под твои критерии' },
  { text: 'Ускоряешь сорсинг в несколько раз с помощью ИИ-инструментов' },
  { text: 'Сорсинг становится процессом, а не лотереей' },
];

// Course content
const courseContent = [
  {
    num: '1️⃣',
    title: 'Keepa от A до Я',
    subtitle: '6 видео-уроков',
    items: [
      'как читать графики правильно',
      'как видеть продажи, конкуренцию и риски',
      'как не брать «красивые, но мёртвые» товары',
    ],
    icon: BarChart3,
    color: 'blue',
  },
  {
    num: '2️⃣',
    title: 'Поиск товаров под арбитраж',
    subtitle: '7 видео-уроков',
    items: [
      'где реально искать товары',
      'как фильтровать мусор',
      'как понимать, что товар масштабируем',
    ],
    icon: Search,
    color: 'violet',
  },
  {
    num: '3️⃣',
    title: 'Домашние задания',
    subtitle: '4 практики',
    items: [
      'закрепление каждого ключевого блока',
      'не «посмотрел и забыл», а внедрил',
    ],
    icon: FileText,
    color: 'amber',
  },
  {
    num: '4️⃣',
    title: 'Поиск оптовых поставщиков',
    subtitle: '2 видео-урока',
    items: [
      'где искать оптовиков быстро',
      'как писать, чтобы отвечали',
      'как подбирать бренды под опт, а не ритейл',
    ],
    icon: Package,
    color: 'emerald',
  },
  {
    num: '5️⃣',
    title: 'Личные созвоны',
    subtitle: '3 сессии с Вадимом',
    items: [
      'разбор твоего сорсинга',
      'исправление ошибок',
      'ответы на вопросы по товарам и поставщикам',
    ],
    icon: Phone,
    color: 'rose',
  },
];

// Who it's for
const forWhom = [
  'Тем, кто хочет реально масштабироваться',
  'Тем, кто устал искать товары вручную',
  'Тем, кто хочет системный сорсинг под EU / US',
];

const notForWhom = [
  'Тем, кто ищет «быстрые деньги»',
  'Тем, кто не готов внедрять',
];

// Result items
const resultItems = [
  'Системный поиск товаров вместо хаоса',
  'Понимание Keepa как инструмента решений',
  'Ускоренный сорсинг с помощью ИИ-инструментов',
  'Высокий процент ответов от поставщиков',
  'Масштабируемый процесс, а не лотерея',
  'Экономия десятков часов и тысяч ошибок',
];

const colorStyles = {
  blue: 'from-blue-500 to-indigo-600',
  violet: 'from-violet-500 to-purple-600',
  amber: 'from-amber-500 to-orange-600',
  emerald: 'from-emerald-500 to-teal-600',
  rose: 'from-rose-500 to-pink-600',
};

const SourcingPackPage = () => {
  const [checkoutData, setCheckoutData] = useState({ isOpen: false, packageId: '', packageName: '', price: 0 });
  const { getPrice } = usePrices();
  const price = getPrice('sourcing_pack');

  const handleCheckout = () => {
    setCheckoutData({ 
      isOpen: true, 
      packageId: 'sourcing-pack', 
      packageName: 'Sourcing Pack', 
      price: price 
    });
  };

  const closeCheckout = () => {
    setCheckoutData({ isOpen: false, packageId: '', packageName: '', price: 0 });
  };

  return (
    <div className="min-h-screen bg-[#050506]">
      {/* Checkout Modal */}
      <CheckoutModal 
        isOpen={checkoutData.isOpen}
        onClose={closeCheckout}
        packageId={checkoutData.packageId}
        packageName={checkoutData.packageName}
        price={checkoutData.price}
        discountMode={false}
      />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl blur opacity-40 group-hover:opacity-70 transition-opacity" />
                <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
                  <span className="text-black font-black text-lg">A</span>
                </div>
              </div>
              <div>
                <span className="text-white font-bold text-lg group-hover:text-amber-400 transition-colors">Amazon</span>
                <span className="text-zinc-400 font-medium text-lg ml-1">Wholesale</span>
              </div>
            </Link>
            
            <Link 
              to="/catalog"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-700 transition-all"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">В каталог</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Product Card */}
          <div className="relative mb-16">
            {/* Glow */}
            <div className="absolute -inset-3 bg-gradient-to-r from-emerald-500/20 via-teal-500/30 to-emerald-500/20 rounded-[2rem] blur-2xl" />
            
            {/* Card */}
            <div className="relative bg-gradient-to-b from-zinc-900 to-zinc-950 rounded-[1.5rem] border border-emerald-500/30 overflow-hidden">
              
              {/* Header */}
              <div className="relative p-8 sm:p-10 bg-gradient-to-b from-emerald-500/10 to-transparent border-b border-emerald-500/20">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                  <div className="flex items-center gap-5">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-xl shadow-emerald-500/30">
                      <Search className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white">Sourcing Pack</h1>
                      <p className="text-emerald-300 text-base sm:text-lg mt-1">
                        Поиск товаров и поставщиков для Amazon — системно и масштабируемо
                      </p>
                    </div>
                  </div>
                  <div className="text-left lg:text-right">
                    <p className="text-5xl sm:text-6xl font-black text-emerald-400">${price}</p>
                    <p className="text-zinc-500 text-sm mt-1">единоразово</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 sm:p-10 space-y-10">
                
                {/* What is it */}
                <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <Layers className="w-5 h-5 text-emerald-400" />
                    </span>
                    Что это
                  </h3>
                  <p className="text-zinc-300 text-lg sm:text-xl leading-relaxed">
                    <span className="text-white font-semibold">Sourcing Pack</span> — это набор видео-уроков и практических разборов, который с нуля обучает:
                  </p>
                  <ul className="mt-4 space-y-3">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-1" />
                      <span className="text-zinc-300 text-base sm:text-lg">находить прибыльные товары для арбитража и опта</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-1" />
                      <span className="text-zinc-300 text-base sm:text-lg">быстро искать реальных поставщиков</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-1" />
                      <span className="text-zinc-300 text-base sm:text-lg">автоматизировать и ускорять сорсинг в несколько раз</span>
                    </li>
                  </ul>
                  <p className="mt-4 text-emerald-400 font-semibold text-base sm:text-lg">
                    Без хаоса, без «на глаз», без бесконечных таблиц.
                  </p>
                </div>

                {/* Why needed */}
                <div className="p-6 rounded-2xl bg-gradient-to-r from-amber-500/10 to-amber-900/10 border border-amber-500/30">
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                    <Target className="w-6 h-6 text-amber-400" />
                    Зачем нужен
                  </h3>
                  <p className="text-zinc-300 text-base sm:text-lg mb-4">
                    Поставщики и товары есть всегда.<br />
                    <span className="text-amber-400 font-semibold">Проблема не в рынке, а в том, как вы ищете.</span>
                  </p>
                  <p className="text-zinc-400 text-base mb-3">Большинство селлеров:</p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-1" />
                      <span className="text-zinc-400 text-sm sm:text-base">тратят часы на мусор</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-1" />
                      <span className="text-zinc-400 text-sm sm:text-base">пишут не тем брендам</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-1" />
                      <span className="text-zinc-400 text-sm sm:text-base">не понимают, какие ASINы реально стоят внимания</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-1" />
                      <span className="text-zinc-400 text-sm sm:text-base">не могут масштабировать сорсинг</span>
                    </li>
                  </ul>
                  <p className="mt-4 text-white font-semibold text-base sm:text-lg">
                    Этот продукт решает именно это.
                  </p>
                </div>

                {/* Before/After Grid */}
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Before */}
                  <div className="p-6 rounded-2xl bg-red-500/5 border border-red-500/20">
                    <h4 className="text-red-400 font-bold text-xl sm:text-2xl mb-5 flex items-center gap-3">
                      <X className="w-6 h-6" />
                      Что у вас было ДО
                    </h4>
                    <div className="space-y-4">
                      {beforeItems.map((item, i) => (
                        <div key={i} className="flex items-start gap-4">
                          <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <X className="w-4 h-4 text-red-400" />
                          </div>
                          <span className="text-zinc-400 text-base sm:text-lg">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* After */}
                  <div className="p-6 rounded-2xl bg-emerald-500/5 border border-emerald-500/20">
                    <h4 className="text-emerald-400 font-bold text-xl sm:text-2xl mb-5 flex items-center gap-3">
                      <Check className="w-6 h-6" />
                      Что будет ПОСЛЕ
                    </h4>
                    <div className="space-y-4">
                      {afterItems.map((item, i) => (
                        <div key={i} className="flex items-start gap-4">
                          <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-4 h-4 text-emerald-400" />
                          </div>
                          <span className="text-zinc-300 text-base sm:text-lg">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* What's Included */}
                <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6 flex items-center gap-3">
                    <Video className="w-7 h-7 text-emerald-400" />
                    Что входит в Sourcing Pack
                  </h3>
                  <div className="space-y-4">
                    {courseContent.map((block) => (
                      <div 
                        key={block.num}
                        className="p-5 rounded-xl bg-zinc-800/50 border border-zinc-700/50"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                          <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br ${colorStyles[block.color]} flex items-center justify-center flex-shrink-0`}>
                            <block.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2">
                              <span className="text-lg">{block.num}</span>
                              <h4 className="text-white font-bold text-base sm:text-lg">{block.title}</h4>
                              <span className="text-zinc-500 text-sm">— {block.subtitle}</span>
                            </div>
                            <ul className="space-y-1.5">
                              {block.items.map((item, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                                  <span className="text-zinc-400 text-sm">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Result Section */}
                <div className="p-6 sm:p-8 rounded-2xl bg-gradient-to-b from-emerald-500/10 to-emerald-900/10 border border-emerald-500/30">
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6 flex items-center gap-3">
                    <Rocket className="w-7 h-7 text-emerald-400" />
                    РЕЗУЛЬТАТ
                  </h3>
                  <p className="text-zinc-300 text-base sm:text-lg mb-4">После прохождения продукта вы:</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {resultItems.map((item, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-zinc-800/50">
                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-zinc-300 text-sm sm:text-base">{item}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-6 text-emerald-400 font-bold text-lg sm:text-xl text-center">
                    Итог: сорсинг как системный процесс, а не бесконечная лотерея.
                  </p>
                </div>

                {/* Why $600 */}
                <div className="p-6 rounded-2xl bg-zinc-800/50 border border-zinc-700">
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                    <DollarSign className="w-6 h-6 text-emerald-400" />
                    Почему это стоит ${price}
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span className="text-zinc-300 text-base sm:text-lg">Один нормальный поставщик = месяцы работы</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span className="text-zinc-300 text-base sm:text-lg">Один масштабируемый товар = тысячи $ оборота</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span className="text-zinc-300 text-base sm:text-lg">Этот пакет экономит десятки часов и тысячи ошибок</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span className="text-zinc-300 text-base sm:text-lg">Цена ниже стоимости одного неудачного месяца сорсинга</span>
                    </li>
                  </ul>
                </div>

                {/* Who it's for */}
                <div className="grid lg:grid-cols-2 gap-6">
                  <div className="p-6 rounded-2xl bg-emerald-500/5 border border-emerald-500/20">
                    <h4 className="text-emerald-400 font-bold text-lg sm:text-xl mb-4 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Кому подойдёт
                    </h4>
                    <ul className="space-y-3">
                      {forWhom.map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                          <span className="text-zinc-300 text-base">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="p-6 rounded-2xl bg-red-500/5 border border-red-500/20">
                    <h4 className="text-red-400 font-bold text-lg sm:text-xl mb-4 flex items-center gap-2">
                      <XCircle className="w-5 h-5" />
                      Кому НЕ подойдёт
                    </h4>
                    <ul className="space-y-3">
                      {notForWhom.map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                          <span className="text-zinc-400 text-base">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* After Payment Block */}
                <AfterPaymentBlock />

                {/* CTA Section */}
                <div className="text-center space-y-6 pt-8 border-t border-zinc-800">
                  
                  {/* Price Block */}
                  <div className="inline-block p-6 sm:p-8 rounded-2xl bg-gradient-to-b from-emerald-500/10 to-emerald-900/20 border border-emerald-500/30">
                    <p className="text-5xl sm:text-6xl font-black text-emerald-400 mb-4">${price}</p>
                    
                    {/* Buy Button */}
                    <button
                      onClick={handleCheckout}
                      data-testid="sourcing-pack-buy-btn"
                      className="w-full flex items-center justify-center gap-3 px-10 py-5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-black text-xl rounded-2xl hover:shadow-2xl hover:shadow-emerald-500/30 hover:-translate-y-1 transition-all duration-300 mb-4"
                    >
                      <CreditCard className="w-6 h-6" />
                      <span>Купить Sourcing Pack</span>
                      <ArrowRight className="w-6 h-6" />
                    </button>
                    
                    {/* Sub-CTA text */}
                    <p className="text-zinc-500 text-sm">
                      Без подписки • мгновенный доступ • безопасная оплата
                    </p>
                  </div>
                  
                  {/* Guarantee */}
                  <div className="flex items-center justify-center gap-2 text-zinc-500 text-base">
                    <ShieldCheck className="w-5 h-5 text-emerald-400" />
                    <span>Безопасная оплата через Stripe</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Back to catalog */}
          <div className="text-center">
            <Link 
              to="/catalog"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-700 transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Вернуться в каталог</span>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SourcingPackPage;
